import { css } from '@emotion/react';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

import { useBooking, useDisplayCityTaxOverride } from '@/booking';
import { useIsBookingManagementLinksSupported } from '@/booking-management';
import { useCurrentBookingMembershipDiscountDetail } from '@/booking-membership/hooks/use-current-booking-membership-discount-detail.hook';
import {
  BookingBreakdownPrimaryTable,
  BookingBreakdownRoomsTable,
  BookingBreakdownTaxTotalsTable,
} from '@/booking/components/BookingBreakdown.component';
import CancellationPolicy from '@/booking/components/CancellationPolicy.component';
import CancellationPolicyPrePurchaseBody from '@/booking/components/CancellationPolicyPrePurchaseBody.component';
import { CityTaxLineItemOverride } from '@/booking/components/CityTaxLineItemOverride.component';
import { BookingRequest } from '@/booking/interfaces';
import { Flex } from '@/box';
import { joinPresent } from '@/common/utils/string';
import { DataTableRow, StandoutDataTableRow } from '@/core/components';
import { CurrencyPreview } from '@/currency-preview';
import { useHotel } from '@/hotel';
import { useHotelAddressAsString } from '@/hotel/hooks/use-hotel-address.hook';
import { useTranslation } from '@/i18n';
import { AnchorLink } from '@/ui/controls';
import { Divider } from '@/ui/layout';
import { Spacer, Stack } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { BodyCopy, BodySmall, Heading } from '@/ui/typography';

import { AlternativeCurrencyConfirmationWarning } from '.';

interface BookingConfirmationDetailsProps {
  bookingRequest: BookingRequest;
  isAmendedBooking?: boolean;
}

export const BookingConfirmationDetails: React.FC<
  BookingConfirmationDetailsProps
> = (props) => {
  const booking = useBooking(props.bookingRequest);
  const hotel = useHotel(props.bookingRequest.hotelSlug);
  const address = useHotelAddressAsString(props.bookingRequest.hotelSlug);
  const displayCityTaxOverride = useDisplayCityTaxOverride(
    props.bookingRequest.hotelSlug
  );
  const {
    componentProperties: { confirmation },
  } = useTheme();

  const displayBookingManagementLinks = useIsBookingManagementLinksSupported();

  const { t } = useTranslation([
    'confirmation',
    'common',
    'bookingSummary',
    'taxes',
    'bookingManagement',
    'bookingMembership',
  ]);

  const { discountDetail, includesBundledMembership, membershipPrice } =
    useCurrentBookingMembershipDiscountDetail();

  if (!booking || !hotel) return null;

  const { urls } = hotel;

  return (
    <Stack
      css={css({
        'div[role="separator"]':
          confirmation?.bookingConfirmationAdditionalDetailsContainer?.divider,
        rowGap:
          confirmation?.bookingConfirmationAdditionalDetailsContainer?.rowGap,
        '@media all and (max-width: 730px)': {
          rowGap:
            confirmation?.bookingConfirmationAdditionalDetailsContainer?.mobile
              ?.rowGap,
        },
      })}
      s="m"
    >
      <div>
        <BodyCopy
          css={{
            fontWeight: 700,
            ...confirmation?.bookingConfirmationDetailsCard
              ?.customerNameHeading,
          }}
        >
          {booking.firstRoomStay.billerProfile.fullName}
        </BodyCopy>
        <Spacer s="xs" />
        <Heading
          as="h2"
          style="heading5"
          css={{
            ...confirmation?.bookingConfirmationDetailsCard
              ?.bookingNumberHeading,
            '@media all and (max-width: 730px)':
              confirmation?.bookingConfirmationDetailsCard?.bookingNumberHeading
                ?.mobile,
          }}
        >
          {props.isAmendedBooking
            ? t('bookingManagement:confirmation.bookingNumber', {
                bookingNumber: booking?.pmsId,
              })
            : t('confirmation:bookingNumber', {
                bookingNumber: booking?.pmsId,
              })}
        </Heading>
        <Spacer s="xxs" />
        <BodyCopy
          css={confirmation?.bookingConfirmationDetailsCard?.addressHeading}
        >
          {joinPresent([hotel.name, address], ' - ')}
        </BodyCopy>
      </div>
      <Divider />
      <BookingBreakdownPrimaryTable
        {...props.bookingRequest}
        showCheckInTimes
        showTotalNightCount
      />
      <Divider />
      <BookingBreakdownRoomsTable
        {...props.bookingRequest}
        wrapElementsInContainer={false}
        displayConfirmationNumber={true}
        pricePerNightMode="actual"
        separator={<Divider />}
      />
      {booking.firstTotalChargeBreakdown.charity && (
        <>
          <Divider />
          <DataTableRow
            label={t('bookingSummary:labels.room.charityDonation')}
            value={
              <CurrencyPreview
                amount={booking.firstTotalChargeBreakdown.charity}
              />
            }
            testId="charity-donation"
          />
        </>
      )}
      <Divider />
      <BookingBreakdownTaxTotalsTable {...props.bookingRequest} />
      <Divider />
      {includesBundledMembership ? (
        <>
          <DataTableRow
            title={t('bookingMembership:summary.payment.title')}
            label={t('bookingMembership:summary.payment.label')}
            description={t('bookingMembership:summary.payment.description')}
            value={
              <CurrencyPreview
                amount={booking.firstTotalChargeBreakdown.grandTotal}
              />
            }
            valueFontWeight="bold"
          />
          <Divider />
          <DataTableRow
            title={t('bookingMembership:confirmation.title')}
            label={
              <Flex gap="6px" alignItems="center">
                <Image
                  src="/disloyalty/disloyalty-full-text-green.svg"
                  alt="disloyalty"
                  width={108}
                  height={20}
                />
                <BodySmall>
                  {t('bookingMembership:summary.description')}
                </BodySmall>
              </Flex>
            }
            value={<CurrencyPreview amount={membershipPrice} />}
            description={t('bookingMembership:confirmation.description')}
            valueFontWeight="bold"
            hideColon
            opacity={1}
          />
        </>
      ) : (
        <StandoutDataTableRow
          label={t('bookingSummary:labels.overallTotal')}
          value={
            <CurrencyPreview
              amount={booking.firstTotalChargeBreakdown.grandTotal}
            />
          }
          css={{
            '.label':
              confirmation?.bookingConfirmationDetailsCard?.totalDataRow?.label,
            '.value':
              confirmation?.bookingConfirmationDetailsCard?.totalDataRow?.value,
          }}
        />
      )}
      <Divider />
      {displayCityTaxOverride ? (
        <>
          <CityTaxLineItemOverride
            hotelReferenceId={props.bookingRequest.hotelSlug}
            standout
          />
          <Divider />
        </>
      ) : null}
      {displayBookingManagementLinks ? (
        <>
          <div>
            <Heading
              style="heading6"
              as="h3"
              css={{
                paddingBottom: '10px',
                ...confirmation?.bookingConfirmationDetailsCard?.manageMyBooking
                  ?.title,
              }}
            >
              {t('bookingManagement:confirmation.manageMyBookingTitle')}
            </Heading>
            <BodyCopy
              css={{
                paddingBottom: '10px',
                ...confirmation?.bookingConfirmationDetailsCard?.manageMyBooking
                  ?.description,
              }}
            >
              {t('bookingManagement:confirmation.manageMyBookingDescription')}
            </BodyCopy>
            <Link
              href="/account/login"
              css={{
                textDecoration: 'underline',
                ...confirmation?.bookingConfirmationDetailsCard?.manageMyBooking
                  ?.link,
              }}
            >
              {t('bookingManagement:confirmation.manageBookingButtonLabel')}
            </Link>
          </div>
          <Divider />
        </>
      ) : null}
      <div>
        <AlternativeCurrencyConfirmationWarning
          total={booking.firstTotalChargeBreakdown.grandTotal}
          paymentTime={booking.paymentTime}
        />
        <div>
          <CancellationPolicy
            hotelReferenceId={props.bookingRequest.hotelSlug}
            css={{
              div: {
                span: confirmation?.bookingConfirmationDetailsCard
                  ?.paymentCancellationPolicy?.description,
              },
              h3: {
                span: confirmation?.bookingConfirmationDetailsCard
                  ?.paymentCancellationPolicy?.title,
              },
              paddingBottom: '10px',
            }}
          >
            <CancellationPolicyPrePurchaseBody
              hotelReferenceId={props.bookingRequest.hotelSlug}
            />
          </CancellationPolicy>
        </div>
        {urls.terms && (
          <BodyCopy
            css={{
              ...confirmation?.bookingConfirmationDetailsCard
                ?.termsAndConditions,
            }}
          >
            <AnchorLink href={urls.terms} openInNewTab>
              {t('common:legal.termsAndConditions')}
            </AnchorLink>
          </BodyCopy>
        )}
      </div>
    </Stack>
  );
};
